/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving an individual patient.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

 /*
  * Used to register the logics so that they are included in the store. 
  */
import reducerRegistry from '../ReducerRegistry';

/*
 * Used to get access to the API types and requests
 */
import * as Dtos from '../../api/dtos';

/**
 * Enumeration used to determine the state of a request.
 */
import { IRequestState, RequestState } from '.';

/**
 * Used to create a get request to the servicestack API.
 */
import { createGetRequest } from 'src/utils/createRequest';

/**
 * Typings
 */
import { ILogicSideEffect } from 'src/utils/logicTypes';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IIndividualSubtermsAndGradesState {
    version: Dtos.Version | null;
    category: Dtos.Category | null;
    parentTerm: Dtos.Term | null;
    term: Dtos.Term | null;
    terms: Record<number, Dtos.Term>;
    grades: Record<number, Dtos.Grade>;
    termsView: number[];
    gradesView: number[];
    loadState: IRequestState;
}

interface IInitialSubtermsAndGradesState extends Record<number, IIndividualSubtermsAndGradesState> {
}

export interface ISubtermsAndGradesState {
   subtermsAndGrades: IInitialSubtermsAndGradesState;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const SubtermsAndGradesInitialIndividualState: IIndividualSubtermsAndGradesState = {
    grades: {},
    terms: {},
    version: null,
    category: null,
    parentTerm: null,
    term: null,
    gradesView: [],
    termsView: [],
    loadState: {
        state: RequestState.None
    }
};

const SubtermsAndGradesInitialState: IInitialSubtermsAndGradesState = {
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useSubtermsAndGradesSelector: TypedUseSelectorHook<ISubtermsAndGradesState> = useSelector;

export const SubtermsAndGradesSelectors = {
    selectVersion: (state: ISubtermsAndGradesState, termId: number) => state.subtermsAndGrades[termId] ? state.subtermsAndGrades[termId].version : SubtermsAndGradesInitialIndividualState.version,
    selectCategory: (state: ISubtermsAndGradesState, termId: number) => state.subtermsAndGrades[termId] ? state.subtermsAndGrades[termId].category : SubtermsAndGradesInitialIndividualState.category,
    selectParentTerm: (state: ISubtermsAndGradesState, termId: number) => state.subtermsAndGrades[termId] ? state.subtermsAndGrades[termId].parentTerm : SubtermsAndGradesInitialIndividualState.parentTerm,
    selectTerm: (state: ISubtermsAndGradesState, termId: number) => state.subtermsAndGrades[termId] ? state.subtermsAndGrades[termId].term : SubtermsAndGradesInitialIndividualState.term,
    selectGradesView: (state: ISubtermsAndGradesState, termId: number) => state.subtermsAndGrades[termId] ? state.subtermsAndGrades[termId].gradesView : SubtermsAndGradesInitialIndividualState.gradesView,
    selectTermsView: (state: ISubtermsAndGradesState, termId: number) => state.subtermsAndGrades[termId] ? state.subtermsAndGrades[termId].termsView : SubtermsAndGradesInitialIndividualState.termsView,
    selectGradeFromView: (state: ISubtermsAndGradesState, termId: number, gradeId: number) => state.subtermsAndGrades[termId]  && state.subtermsAndGrades[termId].grades[gradeId] ? state.subtermsAndGrades[termId].grades[gradeId] : null,
    selectTermFromView: (state: ISubtermsAndGradesState, termId: number, subtermId: number) => state.subtermsAndGrades[termId]  && state.subtermsAndGrades[termId].terms[subtermId] ? state.subtermsAndGrades[termId].terms[subtermId] : null,
    selectLoadState: (state: ISubtermsAndGradesState, termId: number) => state.subtermsAndGrades[termId] ? state.subtermsAndGrades[termId].loadState : SubtermsAndGradesInitialIndividualState.loadState,
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class GradeReducer extends ImmerReducer<IInitialSubtermsAndGradesState> {
    public loadByTermId(termId: number) {
        if (!this.draftState[termId]) {
            this.draftState[termId] = {...SubtermsAndGradesInitialIndividualState};
        }

        this.draftState[termId].loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(version: Dtos.Version, category: Dtos.Category, parentTerm: Dtos.Term | null,  term: Dtos.Term, terms: Dtos.Term[], grades: Dtos.Grade[]) {
        const termId = term.id;

        if (!this.draftState[termId]) {
            this.draftState[termId] = {...SubtermsAndGradesInitialIndividualState};
        }

        this.draftState[termId].version = version;
        this.draftState[termId].category = category;
        this.draftState[termId].parentTerm = parentTerm;
        this.draftState[termId].term = term;

        this.draftState[termId].terms = {};
        this.draftState[termId].termsView = [];

        for (const t of terms) {
            this.draftState[termId].terms[t.id] = t;
            this.draftState[termId].termsView.push(t.id);
        }

        this.draftState[termId].grades = {};
        this.draftState[termId].gradesView = [];

        for (const grade of grades) {
            this.draftState[termId].grades[grade.id] = grade;
            this.draftState[termId].gradesView.push(grade.id);
        }

        this.draftState[termId].loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(termId: number, responseStatus: Dtos.ResponseStatus) {
        if (!this.draftState[termId]) {
            this.draftState[termId] = {...SubtermsAndGradesInitialIndividualState};
        }

        this.draftState[termId].version = SubtermsAndGradesInitialIndividualState.version;
        this.draftState[termId].category = SubtermsAndGradesInitialIndividualState.category;
        this.draftState[termId].term = SubtermsAndGradesInitialIndividualState.term;
        this.draftState[termId].parentTerm = SubtermsAndGradesInitialIndividualState.parentTerm;
        this.draftState[termId].terms = SubtermsAndGradesInitialIndividualState.terms;
        this.draftState[termId].termsView = SubtermsAndGradesInitialIndividualState.termsView;
        this.draftState[termId].grades = SubtermsAndGradesInitialIndividualState.grades;
        this.draftState[termId].gradesView = SubtermsAndGradesInitialIndividualState.gradesView;
        this.draftState[termId].loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear(termId: number) {
        delete this.draftState[termId];
    }

    public clearAll() {
        return {...SubtermsAndGradesInitialState};
    }
}

export const SubtermsAndGradesActions = createActionCreators(GradeReducer);
const reducer = createReducerFunction(GradeReducer, SubtermsAndGradesInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const SubtermsAndGradesApi = {
    loadByTermId: createGetRequest(
        Dtos.GetSubtermsAndGradesByTermId,
        (termId: number) => ({
            termId
        })
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const SubtermsAndGradesLogic = {
    loadByTermId: createLogic({
        type: SubtermsAndGradesActions.loadByTermId.type,
        latest: true,
        process: async ({ action }: ILogicSideEffect<ISubtermsAndGradesState, typeof SubtermsAndGradesActions.loadByTermId>, dispatch, done) => {
            try {
                const response = await SubtermsAndGradesApi.loadByTermId(action.payload);

                dispatch(SubtermsAndGradesActions.loadSuccess(
                    response.version,
                    response.category,
                    response.parentTerm,
                    response.term,
                    response.terms,
                    response.grades
                ));
            }
            catch (error) {
                dispatch(SubtermsAndGradesActions.loadFailure(action.payload, error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    SubtermsAndGradesLogic.loadByTermId
]

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('subtermsAndGrades', reducer, logics);


// WEBPACK FOOTER //
// ./src/store/reducers/subtermsAndGrades.ts